<template>
  <div class="flex flex-col">
    <h2 class="text-2xl font-semibold">
      {{ isEdit ? 'Editar Campaña' : 'Nueva Campaña de Email' }}
    </h2>

    <div v-if="!loading" class="card mt-6">
      <form @submit.prevent="save">
        <!-- Subject -->
        <div class="flex flex-col w-full mb-4">
          <label for="subject" class="font-medium mb-1">Asunto del Email *</label>
          <input
            id="subject"
            type="text"
            v-model="form.subject"
            placeholder="Ej: Oferta especial para dueños de gatos"
            required
          />
          <validation-errors :errors="errorFor('subject')"></validation-errors>
        </div>

        <!-- Content -->
        <div class="flex flex-col w-full mb-4">
          <label for="content" class="font-medium mb-1">Contenido del Email *</label>

          <!-- Placeholder buttons -->
          <div class="mb-2 flex flex-wrap gap-2">
            <button
              v-for="placeholder in availablePlaceholders"
              :key="placeholder.key"
              @click.prevent="insertPlaceholder(placeholder.key)"
              type="button"
              class="text-xs px-2 py-1 bg-primary-100 hover:bg-primary-200 text-primary-700 rounded border border-primary-300 smooth"
            >
              + {{ placeholder.label }}
            </button>
          </div>

          <TiptapEditor
            ref="tiptapEditor"
            v-model="form.content"
          />

          <p class="text-xs text-gray-500 mt-2">
            Usa los botones arriba para insertar variables personalizadas que se reemplazarán con datos reales del cliente.
          </p>
          <validation-errors :errors="errorFor('content')"></validation-errors>
        </div>

        <!-- Theme / Colors -->
        <div class="flex flex-col w-full mb-4">
          <div class="flex justify-between items-center mb-2">
            <label class="font-medium">Diseño y Colores</label>
            <button type="button" @click="resetTheme" class="text-xs text-primary-600 hover:underline">
              Restablecer
            </button>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border border-gray-200 rounded-lg">
            <div v-for="field in themeFields" :key="field.key" class="flex flex-col">
              <label class="text-xs text-gray-600 mb-1">{{ field.label }}</label>
              <div class="flex items-center gap-2">
                <input
                  type="color"
                  v-model="form.theme[field.key]"
                  class="w-10 h-8 rounded border border-gray-200 cursor-pointer p-0"
                />
                <input
                  type="text"
                  v-model="form.theme[field.key]"
                  class="flex-1 text-xs py-1 px-2"
                />
              </div>
            </div>
            <div class="col-span-2 md:col-span-4 flex flex-col">
              <label class="text-xs text-gray-600 mb-1">URL del Logo</label>
              <input type="text" v-model="form.theme.logo_url" class="text-xs py-1 px-2" />
            </div>
          </div>
          <!-- Preview -->
          <div class="mt-3 border border-gray-200 rounded-lg overflow-hidden" :style="{ backgroundColor: form.theme.body_bg }">
            <div class="p-4" :style="{ backgroundColor: form.theme.header_bg }">
              <img :src="form.theme.logo_url" alt="logo" class="h-10 mx-auto" @error="logoPreviewError = true" />
              <p v-if="logoPreviewError" class="text-xs text-white text-center mt-1">No se pudo cargar el logo</p>
            </div>
            <div class="p-4" :style="{ backgroundColor: form.theme.card_bg, color: form.theme.text_color }">
              <p class="text-sm">Vista previa: este es el aspecto que tendrá el correo.</p>
              <a href="#" :style="{ color: form.theme.accent_color }" class="text-sm underline">Enlace de ejemplo</a>
            </div>
          </div>
        </div>

        <!-- Audience Type -->
        <div class="flex flex-col w-full mb-4">
          <label for="audience_type" class="font-medium mb-1">Audiencia *</label>
          <custom-select
            v-model="form.audience_type"
            :options="audienceTypes"
            placeholder="Selecciona la audiencia"
            @update:modelValue="onAudienceChange"
          />
          <validation-errors :errors="errorFor('audience_type')"></validation-errors>
        </div>

        <!-- Custom Client Selection -->
        <div v-if="form.audience_type === 'custom'" class="flex flex-col w-full mb-4">
          <label class="font-medium mb-1">Seleccionar Clientes</label>

          <!-- Selected Clients -->
          <div v-if="selectedClients.length > 0" class="mb-3 p-3 bg-primary-50 rounded-lg border border-primary-200">
            <p class="text-sm font-medium text-primary-900 mb-2">Clientes seleccionados ({{ selectedClients.length }})</p>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="client in selectedClients"
                :key="client.id"
                class="inline-flex items-center px-2 py-1 bg-white rounded border border-primary-300 text-sm"
              >
                {{ client.name }} {{ client.last_name }} ({{ client.email }})
                <button
                  @click="removeClient(client.id)"
                  type="button"
                  class="ml-2 text-error-600 hover:text-error-700"
                >
                  ×
                </button>
              </span>
            </div>
          </div>

          <!-- Search Input -->
          <div class="mb-3">
            <input
              type="text"
              v-model="clientSearch"
              @input="searchClients"
              placeholder="Buscar clientes por nombre o email..."
              class="w-full"
            />
            <p class="text-xs text-gray-500 mt-1">
              Escribe al menos 2 caracteres para buscar clientes
            </p>
          </div>

          <!-- Client Search Results -->
          <div v-if="clientSearch.length >= 2" class="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
            <div v-if="loadingClients" class="p-4 text-center text-gray-500">
              Buscando clientes...
            </div>
            <div v-else-if="availableClients.length === 0" class="p-4 text-center text-gray-500">
              No se encontraron clientes con "{{ clientSearch }}"
            </div>
            <div v-else>
              <button
                v-for="client in availableClients"
                :key="client.id"
                @click="toggleClient(client)"
                type="button"
                :class="[
                  'w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-gray-50 smooth flex justify-between items-center',
                  isClientSelected(client.id) ? 'bg-primary-50' : ''
                ]"
              >
                <span class="text-sm">
                  <span class="font-medium">{{ client.name }} {{ client.last_name }}</span>
                  <span class="text-gray-500 ml-2">{{ client.email }}</span>
                </span>
                <span v-if="isClientSelected(client.id)" class="text-primary-600">✓</span>
              </button>
            </div>
          </div>
          <div v-else-if="selectedClients.length === 0" class="p-4 text-center text-gray-400 border border-gray-200 rounded-lg">
            <p class="text-sm">Busca y selecciona clientes para enviar este email</p>
          </div>
        </div>

        <!-- Audience Preview -->
        <div v-if="form.audience_type" class="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm font-medium text-primary-900">
                Destinatarios estimados
              </p>
              <p v-if="!previewLoading && audienceCount !== null" class="text-2xl font-bold text-primary-600 mt-1">
                {{ audienceCount }}
              </p>
              <p v-else-if="previewLoading" class="text-sm text-primary-600 mt-1">
                Calculando...
              </p>
              <p v-else class="text-sm text-gray-500 mt-1">
                Selecciona una audiencia
              </p>
            </div>
            <button
              v-if="form.audience_type"
              @click="previewAudience"
              type="button"
              class="secondary-button"
            >
              Actualizar
            </button>
          </div>
        </div>

        <!-- Schedule Option -->
        <div class="flex items-center mb-4">
          <input
            id="schedule"
            type="checkbox"
            v-model="shouldSchedule"
            class="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label for="schedule" class="ml-2 text-sm font-medium">
            Programar envío para más tarde
          </label>
        </div>

        <!-- Scheduled Date/Time -->
        <div v-if="shouldSchedule" class="flex flex-col w-full mb-4">
          <label for="scheduled_at" class="font-medium mb-1">Fecha y Hora de Envío</label>
          <input
            id="scheduled_at"
            type="datetime-local"
            v-model="form.scheduled_at"
            :min="minDateTime"
            class="border border-gray-200 py-2 px-3 rounded-lg outline-none smooth w-full focus:shadow-glow"
          />
          <p class="text-xs text-gray-500 mt-1">
            La campaña se enviará automáticamente en la fecha y hora seleccionada
          </p>
          <validation-errors :errors="errorFor('scheduled_at')"></validation-errors>
        </div>

        <!-- Actions -->
        <div class="flex justify-between items-center mt-8 pt-4 border-t">
          <router-link :to="{ name: 'massEmailsIndex' }" class="cancel-button">
            Cancelar
          </router-link>

          <div class="flex space-x-3">
            <button
              v-if="!isEdit"
              @click="saveDraft"
              type="button"
              :disabled="loadingSave"
              class="secondary-button"
            >
              Guardar Borrador
            </button>

            <primary-button :loading="loadingSave">
              {{ getSaveButtonText() }}
            </primary-button>
          </div>
        </div>
      </form>
    </div>

    <!-- Loading state -->
    <div v-else class="card mt-6 py-12 text-center">
      <div class="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
      <p class="text-gray-500 mt-4">Cargando...</p>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import validationErrors from '@/mixins/validationErrors';
import ValidationErrors from '@/components/ui/ValidationErrors.vue';
import PrimaryButton from '@/components/ui/PrimaryButton.vue';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import TiptapEditor from '@/components/TiptapEditor.vue';

export default {
  name: 'MassEmailsForm',

  components: {
    ValidationErrors,
    PrimaryButton,
    CustomSelect,
    TiptapEditor,
  },

  mixins: [validationErrors],

  data() {
    return {
      form: {
        subject: '',
        content: '',
        audience_type: '',
        scheduled_at: null,
        status: 'draft',
        filters: {},
        theme: {
          header_bg: '#492A7D',
          body_bg: '#f7f7f7',
          card_bg: '#ffffff',
          text_color: '#333333',
          accent_color: '#8b5cf6',
          button_bg: '#492A7D',
          button_text: '#ffffff',
          logo_url: 'https://trunimal.com/images/logo-white.svg',
        },
      },
      loading: false,
      loadingSave: false,
      previewLoading: false,
      loadingClients: false,
      audienceCount: null,
      shouldSchedule: false,
      clientSearch: '',
      availableClients: [],
      selectedClients: [],
      searchTimeout: null,
      logoPreviewError: false,
      audienceTypes: [
        { value: 'everyone', label: 'Todos los clientes' },
        { value: 'cats', label: 'Solo dueños de gatos' },
        { value: 'dogs', label: 'Solo dueños de perros' },
        { value: 'both', label: 'Dueños de gatos y perros' },
        { value: 'active_quotes', label: 'Clientes con cotizaciones activas' },
        { value: 'pending_quotes', label: 'Clientes con cotizaciones pendientes' },
        { value: 'cancelled_quotes', label: 'Clientes con cotizaciones canceladas' },
        { value: 'custom', label: 'Selección personalizada' },
      ],
      availablePlaceholders: [
        { key: '{{client_name}}', label: 'Nombre del Cliente' },
        { key: '{{client_last_name}}', label: 'Apellido del Cliente' },
        { key: '{{client_full_name}}', label: 'Nombre Completo' },
        { key: '{{client_email}}', label: 'Email del Cliente' },
        { key: '{{pet_name}}', label: 'Primera Mascota' },
        { key: '{{pet_names}}', label: 'Todas las Mascotas' },
        { key: '{{pet_count}}', label: '# de Mascotas' },
        { key: '{{pet_type}}', label: 'Tipo de Mascota' },
        { key: '{{pet_types}}', label: 'Tipos de Mascotas' },
        { key: '{{pet_breed}}', label: 'Raza de la Mascota' },
        { key: '{{pet_breeds}}', label: 'Razas de las Mascotas' },
      ],
      defaultTheme: {
        header_bg: '#492A7D',
        body_bg: '#f7f7f7',
        card_bg: '#ffffff',
        text_color: '#333333',
        accent_color: '#8b5cf6',
        button_bg: '#492A7D',
        button_text: '#ffffff',
        logo_url: 'https://trunimal.com/images/logo-white.svg',
      },
      themeFields: [
        { key: 'header_bg', label: 'Fondo del Encabezado' },
        { key: 'body_bg', label: 'Fondo General' },
        { key: 'card_bg', label: 'Fondo de la Tarjeta' },
        { key: 'text_color', label: 'Color del Texto' },
        { key: 'accent_color', label: 'Color de Enlaces' },
        { key: 'button_bg', label: 'Fondo de Botones' },
        { key: 'button_text', label: 'Texto de Botones' },
      ],
    };
  },

  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },

    minDateTime() {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 5); // Min 5 minutes from now
      return now.toISOString().slice(0, 16);
    },
  },

  async mounted() {
    if (this.isEdit) {
      await this.loadCampaign();
    }
  },

  methods: {
    async loadCampaign() {
      this.loading = true;
      try {
        const campaign = await api.massEmails.getById(this.$route.params.id);

        // Only allow editing drafts
        if (campaign.status !== 'draft') {
          this.$store.dispatch('errorSnack', 'Solo se pueden editar campañas en borrador');
          this.$router.push({ name: 'massEmailsDetail', params: { id: campaign.id } });
          return;
        }

        this.form = {
          subject: campaign.subject,
          content: campaign.content,
          audience_type: campaign.audience_type,
          scheduled_at: campaign.scheduled_at ? this.formatDateTimeLocal(campaign.scheduled_at) : null,
          status: campaign.status,
          theme: { ...this.defaultTheme, ...(campaign.theme || {}) },
        };

        if (campaign.scheduled_at) {
          this.shouldSchedule = true;
        }

        await this.previewAudience();
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al cargar campaña');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async previewAudience() {
      if (!this.form.audience_type) return;

      // For custom audience, require at least one client
      if (this.form.audience_type === 'custom' && this.selectedClients.length === 0) {
        this.audienceCount = 0;
        return;
      }

      this.previewLoading = true;
      try {
        const payload = {
          audience_type: this.form.audience_type,
        };

        if (this.form.audience_type === 'custom') {
          payload.filters = {
            client_ids: this.selectedClients.map(c => c.id),
          };
        }

        const response = await api.massEmails.preview(payload);
        this.audienceCount = response.total_recipients;
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al calcular audiencia');
        console.error(error);
      } finally {
        this.previewLoading = false;
      }
    },

    onAudienceChange() {
      this.audienceCount = null;
      this.selectedClients = [];
      this.clientSearch = '';
      this.availableClients = [];

      if (this.form.audience_type !== 'custom') {
        this.previewAudience();
      }
    },

    async loadClients(search = '') {
      // Only search if at least 2 characters
      if (search.length < 2) {
        this.availableClients = [];
        return;
      }

      this.loadingClients = true;
      try {
        const response = await api.massEmails.searchClients({ search });
        this.availableClients = response.data;
      } catch (error) {
        this.$store.dispatch('errorSnack', 'Error al cargar clientes');
        console.error(error);
      } finally {
        this.loadingClients = false;
      }
    },

    searchClients() {
      clearTimeout(this.searchTimeout);

      // Clear results if search is too short
      if (this.clientSearch.length < 2) {
        this.availableClients = [];
        return;
      }

      this.searchTimeout = setTimeout(() => {
        this.loadClients(this.clientSearch);
      }, 300);
    },

    toggleClient(client) {
      if (this.isClientSelected(client.id)) {
        this.removeClient(client.id);
      } else {
        this.selectedClients.push(client);
        this.previewAudience();
      }
    },

    removeClient(clientId) {
      this.selectedClients = this.selectedClients.filter(c => c.id !== clientId);
      this.previewAudience();
    },

    isClientSelected(clientId) {
      return this.selectedClients.some(c => c.id === clientId);
    },

    resetTheme() {
      this.form.theme = { ...this.defaultTheme };
      this.logoPreviewError = false;
    },

    insertPlaceholder(placeholder) {
      // Insert placeholder at cursor position in Tiptap
      this.$refs.tiptapEditor.insertText(placeholder);
    },

    async saveDraft() {
      this.form.status = 'draft';
      await this.save();
    },

    async save() {
      if (!this.validateForm()) {
        this.$store.dispatch('errorSnack', 'Por favor completa todos los campos requeridos');
        return;
      }

      this.loadingSave = true;
      try {
        const payload = {
          subject: this.form.subject,
          content: this.form.content,
          theme: this.form.theme,
          audience_type: this.form.audience_type,
          status: this.shouldSchedule ? 'scheduled' : 'draft',
        };

        // Add custom audience filters
        if (this.form.audience_type === 'custom') {
          payload.filters = {
            client_ids: this.selectedClients.map(c => c.id),
          };
        }

        if (this.shouldSchedule && this.form.scheduled_at) {
          payload.scheduled_at = this.form.scheduled_at;
        }

        let response;
        if (this.isEdit) {
          response = await api.massEmails.update(this.$route.params.id, payload);
        } else {
          response = await api.massEmails.create(payload);
        }

        this.$store.dispatch('successSnack', this.isEdit ? 'Campaña actualizada' : 'Campaña creada exitosamente');
        this.$router.push({ name: 'massEmailsDetail', params: { id: response.campaign.id } });
      } catch (error) {
        if (error.response?.status === 422) {
          this.errors = error.response.data.errors;
          this.$store.dispatch('errorSnack', 'Por favor corrige los errores');
        } else {
          this.$store.dispatch('errorSnack', 'Error al guardar campaña');
        }
        console.error(error);
      } finally {
        this.loadingSave = false;
      }
    },

    validateForm() {
      return this.form.subject && this.form.content && this.form.audience_type;
    },

    getSaveButtonText() {
      if (this.isEdit) {
        return this.shouldSchedule ? 'Actualizar y Programar' : 'Actualizar';
      }
      return this.shouldSchedule ? 'Crear y Programar' : 'Crear Campaña';
    },

    formatDateTimeLocal(dateString) {
      const date = new Date(dateString);
      const offset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date - offset);
      return localDate.toISOString().slice(0, 16);
    },
  },
};
</script>
